import { useSyncExternalStore } from 'react'

/**
 * « Un export vient d'être lancé DANS CET ONGLET » — fenêtre courte pendant laquelle la
 * liste « Téléchargements » du header se rafraîchit vite.
 *
 * POURQUOI un store hors React : le signal est émis par la couche REST (`fetchRest` /
 * `useRestMutation`, cf. `lib/hook/tanstackQuery.ts`), qui n'est pas un composant. Il est lu
 * par `useMyExportDownloads`, monté dans le header de TOUTES les pages. Les deux ne se
 * connaissent pas et ne partagent aucun contexte : un module partagé est le seul point de
 * rendez-vous.
 *
 * POURQUOI PAS un `onSuccess` dans chacun des écrans d'export : il y en a une quinzaine, et
 * chacun aurait sa propre version du geste. Le déclencheur est DÉCLARATIF — un drapeau
 * `asyncExport: true` posé sur la définition de l'endpoint (`ApiMutationEndpoint` /
 * `ApiQueryEndpoint`) — et la couche REST en tire seule la conséquence.
 *
 * VOLONTAIREMENT EN MÉMOIRE (jamais sessionStorage) : la fenêtre ne sert qu'à accélérer un
 * rafraîchissement. Un onglet rechargé retombe sur le rythme normal (60 s + focus), ce qui
 * est exactement le comportement voulu — rien n'est perdu, l'export finit par apparaître.
 */
const FAST_POLL_INTERVAL_MS = 10_000

/** Au-delà, la génération n'est plus « en cours » à l'échelle de l'utilisateur. */
const FAST_POLL_WINDOW_MS = 5 * 60_000

/**
 * Booléen STOCKÉ et non recalculé depuis un horodatage : `useSyncExternalStore` exige un
 * snapshot stable d'un appel à l'autre — un `Date.now() - launchedAt < WINDOW` rendrait une
 * valeur qui change sans notification et ferait boucler le rendu.
 */
let windowOpen = false

let closeTimer: ReturnType<typeof setTimeout> | null = null

const listeners = new Set<() => void>()

const notify = (): void => listeners.forEach(listener => listener())

export const ASYNC_EXPORT_FAST_POLL_INTERVAL_MS = FAST_POLL_INTERVAL_MS

export const getAsyncExportWindowOpen = (): boolean => windowOpen

/** Snapshot serveur déterministe (anti-mismatch d'hydratation) : jamais ouvert en SSR. */
export const getAsyncExportWindowServerSnapshot = (): boolean => false

export const subscribeAsyncExportWindow = (listener: () => void): (() => void) => {
  listeners.add(listener)

  return () => {
    listeners.delete(listener)
  }
}

/**
 * Appelé par la couche REST quand une requête portant `asyncExport: true` a réussi. Chaque
 * nouvel export REPOUSSE la fin de fenêtre : lancer trois exports d'affilée ne doit pas
 * fermer le rythme rapide cinq minutes après le PREMIER.
 */
export const signalAsyncExportLaunched = (): void => {
  if (closeTimer) clearTimeout(closeTimer)

  closeTimer = setTimeout(() => {
    closeTimer = null
    windowOpen = false
    notify()
  }, FAST_POLL_WINDOW_MS)

  if (windowOpen) return
  windowOpen = true
  notify()
}

/** Abonnement React — utilisable partout, y compris hors provider. */
export const useAsyncExportWindowOpen = (): boolean =>
  useSyncExternalStore(
    subscribeAsyncExportWindow,
    getAsyncExportWindowOpen,
    getAsyncExportWindowServerSnapshot
  )
